import rungs from '../../../brand/rungs.json'
import type { Billing, BillingInterval, CardAccount, PaidRung, Rung } from '../api/client'
import { formatPrice } from '../catalog/format'

// RUNGS is brand/rungs.json: each rung's name and what it includes, the
// prices and the VAT line, shared with the pricing page so the two never
// disagree. The API's billing.Name says the same names.
export const RUNGS = rungs

// rungName is the rung as she reads it: Free, Payments or Pro.
export function rungName(rung: Rung): string {
  return RUNGS[rung].name
}

// rungPrice is a paid rung's list price in pence for the interval. Only
// Pro is sold yearly; Payments has its monthly price either way.
export function rungPrice(rung: PaidRung, interval: BillingInterval = 'month'): number {
  if (rung === 'pro' && interval === 'year') return RUNGS.pro.price_year_pence
  return RUNGS[rung].price_month_pence
}

// priceLine is "£14 a month", "£290 a year".
export function priceLine(rung: PaidRung, interval: BillingInterval = 'month'): string {
  return amountLine(rungPrice(rung, interval), interval)
}

// amountLine is an amount she pays per interval, e.g. her founder price.
export function amountLine(pence: number, interval: BillingInterval): string {
  return `${formatPrice(pence)} a ${interval}`
}

// A Founder Circle member's terms (ADR-025): Pro at her own price, monthly
// only, with her first months free (a Stripe trial). The API's
// billing.FounderFreeMonths agrees (TestFounderTermsAgreeWithBrandRungs).
export const FOUNDER_PRO_PENCE = RUNGS.founder.pro_price_month_pence
export const FOUNDER_FREE_MONTHS = RUNGS.founder.free_months

// proOffer is Pro's price as she would pay it: "£29 a month", or for a
// founder "£20 a month", and "3 months free, then £20 a month" while her free
// months are still to come.
export function proOffer(billing?: Billing | null): string {
  if (!billing?.founder_member) return priceLine('pro')
  const price = amountLine(FOUNDER_PRO_PENCE, 'month')
  return billing.founder_trial ? `${FOUNDER_FREE_MONTHS} months free, then ${price}` : price
}

// The one VAT line a surface with a price carries.
export const VAT_LINE = `${RUNGS.vat_line}.`

// The one button that sells each rung, on its sheet and the plan card. Pro
// says her price (proOffer): a founder's, with her free months.
export function buyLabel(rung: PaidRung, billing?: Billing | null): string {
  return rung === 'pro' ? `Go Pro, ${proOffer(billing)}` : `Turn on card payments, ${priceLine('card_payments')}`
}

export const PAY_YEARLY = `Pay yearly, ${formatPrice(RUNGS.pro.price_year_pence)}, two months free`
// The lines before a change that charges her saved card today (review,
// 2026-10-25): what today's charge is for and what she pays after it, with
// her billing date rather than "this month", which read as the calendar's.
// Paying yearly moves her billing date to today (spec 1.3).
export const PAY_YEARLY_CONFIRM = `Today you pay ${formatPrice(RUNGS.pro.price_year_pence)}, less the part of this month’s ${formatPrice(RUNGS.pro.price_month_pence)} you haven’t used. Your next ${formatPrice(RUNGS.pro.price_year_pence)} is a year from today. VAT included.`
// Payments to Pro in place charges her stored card at once, so she is asked
// first; her billing date stays (until is it, "24 November").
export function upgradeConfirm(until: string | null): string {
  const days = until ? `the days left until ${until}` : 'the rest of this billing month'
  return `Today you pay the difference between ${formatPrice(RUNGS.card_payments.price_month_pence)} and ${formatPrice(RUNGS.pro.price_month_pence)} for ${days}. After that, ${priceLine('pro')}. VAT included.`
}
export const UPGRADE_CONFIRM_BUTTON = 'Go Pro'
// The Free card's buttons carry no price: each rung's heading beside them
// already says it, and the priced labels wrapped onto two lines on a phone.
export const GO_PRO = 'Go Pro'

// upgradesInPlace is whether going Pro changes her Payments subscription in
// place (charging the stored card at once) rather than opening Checkout.
export function upgradesInPlace(billing: Billing | null | undefined): boolean {
  return billing?.source === 'subscription' && billing.rung === 'card_payments'
}
// The portal's button: the card and the invoices are all the portal does now
// (owner, 2026-10-25), so the button says so.
export const CARD_AND_INVOICES = 'Card and invoices'
// Whose payment: "your last payment" read as a client's to a professional
// who takes card payments (review, 2026-10-25).
export const OVERDUE_LINE = 'Your payment for Harmovi didn’t go through. Update your card.'
// Cancelling Pro, from the plan card (owner, 2026-10-25): the quiet link,
// then the ask with its two real choices. Cancelling never happens in
// Stripe's portal, so she always sees this before card payments can end.
// The review (2026-10-25) reworded the ask: what she loses, and the move
// down as a switch rather than a "keep", since Keep Pro is the way back.
export const CANCEL_PRO = 'Cancel Pro'
export const CANCEL_PRO_ASK = 'Cancel Pro?'
// Pro's areas lock rather than go (owner, 2026-10-25): nothing she set up in
// them is deleted, so the ask says so.
export const CANCEL_PRO_LOCKS = 'Without Pro, the Coach, Dream Week, take-home and tax, and the Freedom Score are locked. Your goal and Dream Week are kept for when you come back.'
export const CANCEL_PRO_LINE = `${CANCEL_PRO_LOCKS} Card payments are part of Pro; you can keep them on their own.`
export const KEEP_CARD_PAYMENTS = `Switch to Payments, ${priceLine('card_payments')}`
export const KEEP_CARD_PAYMENTS_NOTE = 'Pro stops today, and what you haven’t used of it comes off your next bills.'
export const CANCEL_BOTH = 'Cancel Pro and card payments'
// While a payment is overdue she can still cancel (owner, 2026-10-25): it ends
// at period end like any other cancellation, and the payment that failed stays
// due, since Stripe keeps retrying it and the owner still decides on it.
export const STILL_DUE = 'The payment that didn’t go through is still due, and Stripe keeps trying your card.'
export function cancelBothNote(on: string | null, overdue = false): string {
  const until = `Both stay on until ${on ?? 'the end of this billing month'}, then stop.`
  return overdue ? `${until} ${STILL_DUE}` : `${until} No more charges.`
}
// Turning card payments off, on the Payments plan (owner, 2026-10-25): the
// red button on the Card payments card and the sheet it opens, the same
// shape as Cancel Pro.
export const TURN_OFF_CARD_PAYMENTS = 'Turn off card payments'
export const TURN_OFF_ASK = 'Turn off card payments?'
// turnOffLine says when, what stops, that the £14 stops with it, and what
// still works (review, 2026-10-25).
export function turnOffLine(on: string | null): string {
  return `Card payments stay on until ${on ?? 'the end of this billing month'}. Then deposits, pay links and charges to saved cards stop, and so does the ${priceLine('card_payments')}. Links you’ve already sent still work.`
}

// endingLine is the plan card's line once a rung is cancelled at period end
// (review, 2026-10-25): Payments ends as card payments, the thing she turned
// off, and nothing more is charged.
export function endingLine(rung: PaidRung, on: string): string {
  return rung === 'pro' ? `Pro ends on ${on}. No more charges.` : `Card payments end on ${on}. No more charges.`
}
export const CANCELLED = 'Cancelled. Nothing more will be charged.'

// Taking a cancellation back, while the rung is still on.
export function keepLabel(rung: PaidRung): string {
  return rung === 'pro' ? 'Keep Pro' : 'Keep card payments on'
}

// The Pro locks (ProLock): the pill's line, then each area's hint.
export const UNLOCK_WITH_PRO = 'Unlock with Pro'
export const HINT_TODAY = 'Daily recommendations, with the message ready to send'
export const HINT_GOALS = 'Design your week, and a coach who reads your month and says what to change'

// The Coach as the locked Goals screen shows it before she has a reading of
// her own (no goal yet, or her diary not live): one made-up month, marked
// Example, in the Coach's own shape. The figures are docs/17's.
export const COACH_EXAMPLE = {
  summary: 'October is about £340 behind your goal, mostly from two quiet Thursdays.',
  actions: [
    { title: 'Raise brow lamination by £5', worth: 'Worth about £420' },
    { title: 'Fill Thursday afternoons before adding a Saturday', worth: 'Worth about £260' },
  ],
}
export const HINT_EARNINGS = 'Take-home and tax to set aside, and your Freedom Score'

// The order of things on Free → Payments: Stripe's setup first (free), the
// £14 once she turns card payments on after approval. Said on the plan card
// under the Payments rung and on the Card payments card before setup.
export const SETUP_IS_FREE = `Setting up with Stripe is free. The ${formatPrice(RUNGS.card_payments.price_month_pence)} a month starts when you turn card payments on, once Stripe has approved you.`
export const SETUP_UNFINISHED = `You’ve started setting up with Stripe. The ${formatPrice(RUNGS.card_payments.price_month_pence)} a month starts only when you turn card payments on, once Stripe has approved you.`
export const SETUP_IN_REVIEW = `Stripe is checking your details. Once they approve you, turn card payments on for ${priceLine('card_payments')}.`
export const SETUP_APPROVED = 'Stripe has approved you.'
export const TURN_ON_CARD_PAYMENTS = 'Turn on card payments'
// Pro starts with Stripe's setup too (owner, 2026-10-25): Pro includes card
// payments, so like Payments it is bought only once Stripe has approved her.
// The lines say where she is on that road, and that the £29 comes after it.
export const PRO_SETUP_FIRST = `Pro starts with Stripe’s free setup for card payments. The ${formatPrice(RUNGS.pro.price_month_pence)} a month starts when you go Pro, once Stripe has approved you.`
export const PRO_SETUP_UNFINISHED = `Finish setting up with Stripe first. The ${formatPrice(RUNGS.pro.price_month_pence)} a month starts when you go Pro, once Stripe has approved you.`
export const PRO_IN_REVIEW = `Stripe is checking your details. Once they approve you, go Pro for ${priceLine('pro')}.`
export const SET_UP_INCLUDED = 'Card payments are included. Set them up with Stripe in Settings → Plan and payments; it’s free.'

// PaymentsStage is where she is on Free → Payments (review, 2026-10-25):
// Stripe's setup to start, a setup to finish, Stripe checking it, then the
// £14 to buy. With card payments not configured the API decides (buy).
export type PaymentsStage = 'setup' | 'finish' | 'review' | 'buy'

export function paymentsStage(account: CardAccount | null): PaymentsStage {
  if (!account?.available || account.status === 'active') return 'buy'
  if (account.status === 'none') return 'setup'
  if (account.status === 'in_review') return 'review'
  return 'finish'
}

// paymentsButton is the Payments button for her stage: the free setup and
// its finishing carry no price, Stripe's check has no button (nothing she
// taps moves it on), and only the £14 itself is sold. withPrice is for a
// surface with no price heading of its own (the sheet).
export function paymentsButton(account: CardAccount | null, withPrice: boolean): string | null {
  switch (paymentsStage(account)) {
    case 'setup':
      return 'Set up card payments'
    case 'finish':
      return 'Continue setup'
    case 'review':
      return null
    case 'buy':
      return withPrice ? buyLabel('card_payments') : TURN_ON_CARD_PAYMENTS
  }
}

// proButton is the Pro button for her stage on that same road: going Pro
// once Stripe has approved her, until then the setup's own step, on the
// Free card as on the Payments rung beside it (owner, 2026-10-25). withPrice
// is for the sheet, which has no price heading of its own.
export function proButton(account: CardAccount | null, withPrice: boolean, billing?: Billing | null): string | null {
  if (paymentsStage(account) === 'buy') return withPrice ? buyLabel('pro', billing) : GO_PRO
  return paymentsButton(account, withPrice)
}

// proNote is the line under Pro (and on the Pro sheet) before she can buy it.
export function proNote(account: CardAccount | null): string | null {
  if (!account || !account.available) return null
  return { setup: PRO_SETUP_FIRST, finish: PRO_SETUP_UNFINISHED, review: PRO_IN_REVIEW, buy: null }[paymentsStage(account)]
}

// paymentsNote is the line under the Payments rung: where she is on the way
// to taking card payments, and that the £14 comes after Stripe's free setup.
export function paymentsNote(account: CardAccount | null): string | null {
  if (!account || !account.available) return null
  return { setup: SETUP_IS_FREE, finish: SETUP_UNFINISHED, review: SETUP_IN_REVIEW, buy: SETUP_APPROVED }[paymentsStage(account)]
}

// noShowLine is the one-off prompt after a no-show on Free: what her
// deposit policy would have kept, or "part of that" with no policy.
export function noShowLine(depositPence: number): string {
  const kept = depositPence > 0 ? `${formatPrice(depositPence)} of that` : 'part of that'
  return `A deposit would have kept ${kept}. Card payments are ${priceLine('card_payments')}.`
}

// youreOn is what a sheet or the confirming page says once the rung is hers.
export function youreOn(rung: PaidRung): string {
  return rung === 'pro' ? `You’re on ${rungName('pro')}` : 'Card payments are on'
}

// longDate is "14 November", with the year when it isn't this year.
export function longDate(iso: string, timeZone?: string, now: Date = new Date()): string {
  const at = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00Z`) : new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const zone = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? 'UTC' : timeZone
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', timeZone: zone }
  try {
    const year = new Intl.DateTimeFormat('en-GB', { year: 'numeric', timeZone: zone }).format(at)
    if (year !== String(now.getFullYear())) opts.year = 'numeric'
    return new Intl.DateTimeFormat('en-GB', opts).format(at)
  } catch {
    return at.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
  }
}
